import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import WritePostForm from '../components/common/WritePostForm';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { BoardInfo } from '../types/post';

interface LocationState {
  initialCategory?: string;
  availableBoards?: BoardInfo[];
}

const WritePost: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  console.log('@#@#state', state);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (postData: any) => {
    setIsSubmitting(true);
    
    try {
      // API 호출이 WritePostForm에서 이미 완료되었으므로
      // 여기서는 성공 메시지와 리다이렉션만 처리
      alert('게시글이 성공적으로 작성되었습니다!');

      // 해당 카테고리 페이지로 이동
      const redirectPath = getRedirectPath(postData.board, postData.category);
      navigate(redirectPath);
      
    } catch (error) {
      console.error('게시글 작성 실패:', error);
      alert('게시글 작성에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRedirectPath = (board: string, category: string) => {
    const boardRoutes = {
      'notice': '/community/notice',
      'lecture-question': '/community/lecture-questions',
      'info-sharing': '/community/info-sharing',
      'coffee-chat': '/coffee-chat',
      'inquiry': '/mypage/inquiry',
      'study-general': '/community/study',
      'study-exam': '/community/study',
      'study-language': '/community/study',
    };

    return boardRoutes[board as keyof typeof boardRoutes] || '/community';
  };

  const handleCancel = () => {
    if (window.confirm('작성 중인 내용이 있습니다. 정말 취소하시겠습니까?')) {
      navigate(-1);
    }
  };

  // 기본 카테고리와 게시판 설정
  const defaultCategories = [
    { id: 'community', name: '커뮤니티', value: 'community' },
    { id: 'study', name: '스터디', value: 'study' },
    { id: 'mypage', name: '마이페이지', value: 'mypage' },
  ];

  const defaultBoards = [
    { id: 'notice', name: '공지사항', category: 'community', apiCategory: '공지사항' as const },
    { id: 'lecture-question', name: '강의 질문', category: 'community', apiCategory: '질문' as const },
    { id: 'info-sharing', name: '정보 공유', category: 'community', apiCategory: '정보공유' as const },
    { id: 'coffee-chat', name: '커피챗', category: 'community', apiCategory: '커피챗' as const },
    { id: 'inquiry', name: '문의하기', category: 'mypage', apiCategory: '문의하기' as const },
    { id: 'study-general', name: '일반 스터디', category: 'study', apiCategory: '스터디' as const },
    { id: 'study-exam', name: '시험 스터디', category: 'study', apiCategory: '스터디' as const },
    { id: 'study-language', name: '언어 스터디', category: 'study', apiCategory: '스터디' as const },
  ];

  // location.pathname에 따라 availableBoards 필터링
  const getAvailableBoards = () => {
    if (state?.availableBoards) {
      return state.availableBoards;
    }

    const pathname = location.pathname;
    
    if (pathname.startsWith('/community')) {
      return defaultBoards.filter(board => board.category === 'community');
    } else if (pathname.startsWith('/study')) {
      return defaultBoards.filter(board => board.category === 'study');
    } else if (pathname.startsWith('/mypage')) {
      return defaultBoards.filter(board => board.category === 'mypage');
    }
    
    // 기본적으로 모든 게시판 반환
    return defaultBoards;
  };

  const availableBoards = getAvailableBoards();

  // location.pathname에 따라 initialCategory 설정
  const getInitialCategory = () => {
    if (state?.initialCategory) {
      return state.initialCategory;
    }

    const pathname = location.pathname;
    
    if (pathname.startsWith('/community')) {
      return 'community';
    } else if (pathname.startsWith('/study')) {
      return 'study';
    } else if (pathname.startsWith('/mypage')) {
      return 'mypage';
    }
    
    // 기본값
    return 'community';
  };

  const initialCategory = getInitialCategory();
  console.log('@#@#availableBoards', availableBoards);

  return (
    <>
      <Header />
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <BackIcon>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path 
                d="M15 18L9 12L15 6" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </BackIcon>
          뒤로가기
        </BackButton>
        
        <ContentWrapper>
          <WritePostForm
            categories={defaultCategories}
            boards={availableBoards}
            initialCategory={initialCategory}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </ContentWrapper>
      </Container>
      <Footer />
    </>
  );
};

const Container = styled.div`
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  max-width: 1200px; /* theme.layout.containerWidth와 동일 */
  margin: 0 auto;
  padding: 40px 0 128px 0; /* 좌우 여백 제거, 컨텐츠 영역 꽉 채움 */
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 1024px) {
    padding: 32px 0 96px 0;
  }
  
  @media (max-width: 768px) {
    padding: 24px 0 64px 0;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 0 0; /* 왼쪽 패딩 제거 */
  border: none;
  background: transparent;
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  align-self: flex-start;
  margin-left: 0; /* 왼쪽 마진도 제거 */

  &:hover {
    background: rgba(112, 115, 124, 0.08);
    color: #171719;
  }
`;

const BackIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  flex: 1;
`;

export default WritePost;